import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import { mapboxToken } from "../helpers/mapbox_token"

// Renders a single location on a Leaflet map: a marker at latitude/longitude,
// with an optional GeoJSON boundary or radius circle drawn around it. No-op
// when lat/lng are missing. Shared by the studio and administration views.
export default class extends Controller {
  static values = {
    latitude: Number,
    longitude: Number,
    name: String,
    geojson: Object,
    radiusMiles: Number
  }

  connect() {
    this.initializeMap();
  }

  disconnect() {
    if (this.map) {
      this.map.remove();
    }
  }

  initializeMap() {
    if (!this.hasLatitudeValue || !this.hasLongitudeValue) {
      return;
    }

    this.map = L.map(this.element);

    L.tileLayer(`https://api.mapbox.com/styles/v1/allroadstaken/clzbxv6l3002l01p66v9eb66s/tiles/256/{z}/{x}/{y}@2x?access_token=${mapboxToken()}`, {
      maxZoom: 19,
      minZoom: 1,
      attribution: '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(this.map);

    // Add marker for the point location
    const popupContent = this.hasNameValue ? this.nameValue : `${this.latitudeValue}, ${this.longitudeValue}`;
    L.marker([this.latitudeValue, this.longitudeValue])
      .addTo(this.map)
      .bindPopup(popupContent);

    // Add GeoJSON layer if available
    if (this.hasGeojsonValue) {
      const geojsonLayer = L.geoJSON(this.geojsonValue, {
        style: {
          color: '#3388ff',
          weight: 3,
          opacity: 0.7,
          fillOpacity: 0.2
        }
      }).addTo(this.map);

      // Fit the map to show both the marker and the geometry
      const bounds = geojsonLayer.getBounds();
      bounds.extend([this.latitudeValue, this.longitudeValue]);
      this.map.fitBounds(bounds, { padding: [50, 50] });
    } else if (this.hasRadiusMilesValue && this.radiusMilesValue > 0) {
      const center = L.latLng(this.latitudeValue, this.longitudeValue);
      const radiusMeters = this.radiusMilesValue * 1609.344;

      // toBounds takes the full width, and unlike circle.getBounds() works
      // before the map has a view
      this.map.fitBounds(center.toBounds(radiusMeters * 2), { padding: [20, 20] });

      L.circle(center, {
        radius: radiusMeters,
        color: '#3388ff',
        weight: 2,
        opacity: 0.7,
        fillOpacity: 0.15
      }).addTo(this.map);
    } else {
      // No geometry, just center on the point
      this.map.setView([this.latitudeValue, this.longitudeValue], 13);
    }
  }
}
